<template>
  <q-page class="evolution-page">
    <div class="evolution-container">
      <!-- Loading state -->
      <div v-if="loading" class="evolution-container">
        <q-skeleton type="text" width="240px" dark style="font-size: 1.75rem;" />
        <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-top: 24px;">
          <q-skeleton v-for="n in 4" :key="n" type="rect" height="80px" dark style="border-radius: 16px;" />
        </div>
        <q-skeleton type="rect" height="260px" dark style="border-radius: 16px; margin-top: 24px;" />
      </div>

      <!-- Loaded state -->
      <template v-else>
        <!-- Header -->
        <div class="evolution-header">
          <router-link to="/evaluations" class="back-link">
            <q-icon name="arrow_back" size="16px" />
            Evaluaciones
          </router-link>
          <h1 class="evolution-title">
            <q-icon name="trending_up" size="28px" :style="{ color: 'var(--oa-gold-full)' }" />
            Evolucion del Entrenador
          </h1>
          <div v-if="stats?.latestCnap" class="evolution-status">
            <span class="status-label">Estado actual:</span>
            <q-badge
              :label="CNAP_MAP[stats.latestCnap].label"
              :style="{
                background: CNAP_MAP[stats.latestCnap].badgeBg,
                color: CNAP_MAP[stats.latestCnap].badgeColor,
                fontSize: '12px',
                padding: '4px 12px',
                borderRadius: '8px',
              }"
            />
          </div>
        </div>

        <!-- Stats row -->
        <div v-if="stats" class="stats-grid">
          <div class="stat-card">
            <q-icon name="fact_check" size="20px" :style="{ color: 'var(--oa-gold-full)' }" />
            <div>
              <p class="stat-value">{{ stats.total }}</p>
              <p class="stat-label">Evaluaciones</p>
            </div>
          </div>
          <div class="stat-card">
            <q-icon name="emoji_events" size="20px" :style="{ color: 'var(--oa-gold-full)' }" />
            <div>
              <p class="stat-value">{{ stats.avg }}</p>
              <p class="stat-label">Score promedio</p>
            </div>
          </div>
          <div class="stat-card">
            <q-icon name="flag" size="20px" :style="{ color: 'var(--oa-gold-full)' }" />
            <div>
              <p class="stat-value-sm">{{ stats.bestInstrument }}</p>
              <p class="stat-label">Mejor instrumento</p>
            </div>
          </div>
          <div class="stat-card">
            <q-icon name="schedule" size="20px" :style="{ color: 'var(--oa-gold-full)' }" />
            <div>
              <p class="stat-value">{{ stats.daysSinceLast !== null ? stats.daysSinceLast : '-' }}</p>
              <p class="stat-label">{{ stats.daysSinceLast !== null ? 'dias' : 'Ultima eval' }}</p>
            </div>
          </div>
        </div>

        <!-- Instrument filter -->
        <div class="filter-bar">
          <q-btn-toggle
            v-model="selectedInstrument"
            no-caps
            rounded
            unelevated
            toggle-color="amber-8"
            text-color="grey-6"
            :options="filterOptions"
            class="filter-toggle"
          />
        </div>

        <!-- Evolution chart -->
        <div v-if="evolutionData.length > 1" class="chart-section">
          <h2 class="section-title">
            {{ selectedInstrument ? INSTRUMENT_LABELS[selectedInstrument] : 'Evolucion general' }}
          </h2>
          <div class="chart-wrapper" style="height: 260px;">
            <Line :data="lineChartData" :options="lineChartOptions" />
          </div>
        </div>

        <!-- Evaluation history -->
        <div v-if="filteredCycles.length > 0" class="history-section">
          <h2 class="section-title-lg">Evaluaciones recientes</h2>
          <div class="history-list">
            <router-link
              v-for="cycle in filteredCycles.slice(0, 10)"
              :key="cycle.id"
              :to="`/evaluations/results/${cycle.id}`"
              class="history-card"
            >
              <div class="history-card-left">
                <p class="history-instrument">{{ INSTRUMENT_LABELS[cycle.instrument] || cycle.instrument }}</p>
                <div class="history-meta">
                  <span v-if="cycle.completedAt" class="history-meta-item">
                    <q-icon name="event" size="12px" />
                    {{ formatDate(cycle.completedAt) }}
                  </span>
                  <span v-if="cycle.evaluator" class="history-meta-item">
                    <q-icon name="person" size="12px" />
                    {{ cycle.evaluator.firstName }} {{ cycle.evaluator.lastName }}
                  </span>
                  <span class="history-occasion">{{ cycle.occasion }}</span>
                </div>
              </div>
              <div class="history-card-right">
                <template v-if="cycle.finalScore">
                  <span class="history-score">{{ parseFloat(String(cycle.finalScore)).toFixed(1) }}</span>
                  <q-badge
                    :label="CNAP_MAP[getCNAP(parseFloat(String(cycle.finalScore)))].label"
                    :style="{
                      background: CNAP_MAP[getCNAP(parseFloat(String(cycle.finalScore)))].badgeBg,
                      color: CNAP_MAP[getCNAP(parseFloat(String(cycle.finalScore)))].badgeColor,
                      fontSize: '11px',
                      padding: '2px 8px',
                      borderRadius: '6px',
                    }"
                  />
                </template>
                <q-badge v-if="!cycle.completedAt" label="Pendiente" outline />
              </div>
            </router-link>
          </div>
        </div>

        <!-- Empty state -->
        <div v-if="filteredCycles.length === 0 && !loading" class="evolution-empty">
          <q-icon name="fact_check" size="40px" :style="{ color: 'var(--oa-text-disabled)' }" />
          <p class="empty-text">Sin evaluaciones registradas todavia.</p>
        </div>
      </template>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { api } from 'boot/axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'vue-chartjs';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

// ---------- Types ----------
interface CnapConfig {
  label: string;
  badgeBg: string;
  badgeColor: string;
}

interface CycleItem {
  id: string;
  instrument: string;
  finalScore: string | number | null;
  completedAt: string | null;
  occasion: string;
  coach?: { firstName: string; lastName: string } | null;
  evaluator?: { firstName: string; lastName: string } | null;
  areaScores?: Record<string, { normalizedScore: number }> | null;
}

interface TrendsData {
  [instrument: string]: { date: string; score: number; occasion?: string }[];
}

// ---------- Constants ----------
const INSTRUMENT_LABELS: Record<string, string> = {
  live_observation: 'Observacion en Vivo',
  performance_binary: 'Desempeno Operativo',
  protocol_checklist: 'Adherencia a Protocolos',
  routine_execution: 'Ejecucion de Rutinas',
};

const CNAP_MAP: Record<string, CnapConfig> = {
  C: { label: 'Critico', badgeBg: 'rgba(239,68,68,0.15)', badgeColor: '#ef4444' },
  N: { label: 'Necesita Mejora', badgeBg: 'rgba(251,146,60,0.15)', badgeColor: '#fb923c' },
  A: { label: 'Adecuado', badgeBg: 'rgba(52,211,153,0.15)', badgeColor: '#34d399' },
  P: { label: 'Positivo', badgeBg: 'rgba(201,169,110,0.15)', badgeColor: 'var(--oa-gold-full)' },
};

const INSTRUMENT_COLORS: Record<string, string> = {
  live_observation: 'var(--color-aged-gold)',
  performance_binary: '#34d399',
  protocol_checklist: '#60a5fa',
  routine_execution: '#a78bfa',
};

function getCNAP(score: number): string {
  if (score >= 9) return 'P';
  if (score >= 7) return 'A';
  if (score >= 5) return 'N';
  return 'C';
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateShort(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
}

// ---------- Router ----------
const route = useRoute();
const coachId = computed(() => route.params.userId as string);

// ---------- State ----------
const loading = ref(true);
const allCycles = ref<CycleItem[]>([]);
const trends = ref<TrendsData>({});
const selectedInstrument = ref('');

const filterOptions = computed(() => [
  { label: 'Todos', value: '' },
  { label: 'Observacion', value: 'live_observation' },
  { label: 'Desempeno', value: 'performance_binary' },
  { label: 'Protocolos', value: 'protocol_checklist' },
  { label: 'Rutinas', value: 'routine_execution' },
]);

// ---------- Computed ----------
const filteredCycles = computed(() => {
  if (!selectedInstrument.value) return allCycles.value;
  return allCycles.value.filter((c) => c.instrument === selectedInstrument.value);
});

const stats = computed(() => {
  const completed = allCycles.value.filter((c) => c.finalScore);
  if (completed.length === 0) return null;

  const scores = completed.map((c) => parseFloat(String(c.finalScore)));
  const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
  const latest = completed[0];
  const latestDate = latest?.completedAt ? new Date(latest.completedAt) : null;
  const daysSinceLast = latestDate
    ? Math.floor((Date.now() - latestDate.getTime()) / (1000 * 60 * 60 * 24))
    : null;

  // Best instrument by avg score
  const byInstrument: Record<string, number[]> = {};
  for (const c of completed) {
    if (!byInstrument[c.instrument]) byInstrument[c.instrument] = [];
    byInstrument[c.instrument].push(parseFloat(String(c.finalScore)));
  }
  let bestInstrument = '';
  let bestAvg = 0;
  for (const [inst, instScores] of Object.entries(byInstrument)) {
    const instAvg = instScores.reduce((a, b) => a + b, 0) / instScores.length;
    if (instAvg > bestAvg) {
      bestAvg = instAvg;
      bestInstrument = inst;
    }
  }

  return {
    total: completed.length,
    avg: avg.toFixed(1),
    daysSinceLast,
    bestInstrument: INSTRUMENT_LABELS[bestInstrument] ?? bestInstrument,
    latestCnap: latest ? getCNAP(parseFloat(String(latest.finalScore))) : null,
  };
});

const evolutionData = computed(() => {
  const cycles = filteredCycles.value
    .filter((c) => c.finalScore && c.completedAt)
    .slice()
    .reverse();

  return cycles.map((c) => ({
    date: formatDateShort(c.completedAt!),
    score: parseFloat(String(c.finalScore)),
  }));
});

const lineChartData = computed(() => {
  const labels = evolutionData.value.map((d) => d.date);
  const datasets = [
    {
      label: selectedInstrument.value
        ? INSTRUMENT_LABELS[selectedInstrument.value]
        : 'Score general',
      data: evolutionData.value.map((d) => d.score),
      borderColor: selectedInstrument.value
        ? INSTRUMENT_COLORS[selectedInstrument.value] || 'var(--color-aged-gold)'
        : 'var(--color-aged-gold)',
      backgroundColor: 'rgba(201, 169, 110, 0.1)',
      fill: true,
      tension: 0.3,
      pointRadius: 4,
      pointBackgroundColor: selectedInstrument.value
        ? INSTRUMENT_COLORS[selectedInstrument.value] || 'var(--color-aged-gold)'
        : 'var(--color-aged-gold)',
      borderWidth: 2,
    },
  ];
  return { labels, datasets };
});

const lineChartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: 'var(--color-sandy-beige)',
      titleColor: 'var(--color-deep-charcoal)',
      bodyColor: 'var(--color-charcoal-mist)',
      borderColor: 'rgba(61, 55, 50, 0.12)',
      borderWidth: 1,
    },
  },
  scales: {
    y: {
      min: 0,
      max: 10,
      ticks: {
        stepSize: 2,
        color: 'rgba(160, 152, 144, 0.4)',
        font: { size: 11 },
      },
      grid: {
        color: 'rgba(61, 58, 54, 0.1)',
      },
    },
    x: {
      ticks: {
        color: 'rgba(160, 152, 144, 0.4)',
        font: { size: 10 },
      },
      grid: {
        color: 'rgba(61, 58, 54, 0.05)',
      },
    },
  },
}));

// ---------- Data loading ----------
async function fetchEvolution() {
  loading.value = true;
  try {
    const [evoRes, trendsRes] = await Promise.all([
      api.get(`/supervision/evolution/${coachId.value}`),
      api.get(`/supervision/trends/${coachId.value}`).catch(() => ({ data: {} })),
    ]);
    allCycles.value = evoRes.data;
    trends.value = trendsRes.data;
  } catch (err) {
    console.error(err);
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  fetchEvolution();
});
</script>

<style scoped lang="scss">
.evolution-page {
  padding: 24px;
}

.evolution-container {
  max-width: 1000px;
  margin: 0 auto;
}

// === Header ===
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--oa-text-sm);
  color: var(--oa-text-disabled);
  text-decoration: none;
  margin-bottom: 16px;
  transition: color 0.15s ease;

  &:hover {
    color: var(--oa-gold-full);
  }
}

.evolution-header {
  margin-bottom: 24px;
}

.evolution-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: var(--oa-text-xl);
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 0;
}

.evolution-status {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.status-label {
  font-size: var(--oa-text-sm);
  color: var(--oa-text-disabled);
}

// === Stats ===
.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 20px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 16px;
  padding: 16px;
}

.stat-value {
  font-size: var(--oa-text-lg);
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 0;
}

.stat-value-sm {
  font-size: var(--oa-text-sm);
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

.stat-label {
  font-size: var(--oa-text-xs);
  color: var(--oa-text-muted);
  margin: 2px 0 0 0;
}

// === Filter ===
.filter-bar {
  margin-bottom: 20px;
  overflow-x: auto;
}

.filter-toggle {
  background: var(--oa-bg-2);
  border-radius: 12px;
}

// === Chart ===
.chart-section {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
}

.section-title {
  font-size: var(--oa-text-md);
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0 0 16px 0;
}

.chart-wrapper {
  position: relative;
}

// === History ===
.history-section {
  margin-bottom: 24px;
}

.section-title-lg {
  font-size: var(--oa-text-lg);
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0 0 12px 0;
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.history-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 16px;
  padding: 16px;
  text-decoration: none;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: color-mix(in srgb, var(--oa-gold-full) 30%, transparent);
  }
}

.history-card-left {
  min-width: 0;
}

.history-instrument {
  font-size: var(--oa-text-sm);
  font-weight: 500;
  color: var(--oa-text-primary);
  margin: 0;
}

.history-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
  flex-wrap: wrap;
}

.history-meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--oa-text-xs);
  color: var(--oa-text-disabled);
}

.history-occasion {
  font-size: var(--oa-text-xs);
  color: var(--oa-text-disabled);
  text-transform: capitalize;
}

.history-card-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.history-score {
  font-size: var(--oa-text-lg);
  font-weight: 700;
  color: var(--oa-text-primary);
}

// === Empty ===
.evolution-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px;
  text-align: center;
}

.empty-text {
  color: var(--oa-text-muted);
  margin: 12px 0 0 0;
}
</style>
